import {
  DiscountType,
  OrderChannel,
  OrderChannelType,
  OrderConditions,
  OrderStatus,
  OrderType,
} from 'src/app/@core/constants';
import { CalculationUtils } from 'src/app/@core/utils/calculation-utils';
import { Discount } from '../discount';
import { Franchise } from '../franchised';
import { Order } from '../order';
import { ProductCart } from '../product-cart';
import { CustomerTableModel } from '../table-models/customer-table';
import { Tax } from '../tax';
import { TaxExemption } from '../tax-exemption';
import { OrderEntryRequest } from './order-entry-request';

export class OrderRequest {
  code: string;
  salesOrgCode: string;
  branchCode: string;
  customerCode: string;
  customerName: string;
  customerLastName: string;
  customerFiscalId: string;
  exonerationReason?: string;
  exonerationDocumentId?: string;
  exonerationDocumentExpirationDate?: string;
  exemptionReason?: string;
  exemptionDocumentId?: string;
  exemptionExpirationDate?: string;
  routeCode?: string;
  cancellationReasonCode?: string;
  invoiceCode?: string;
  invoiceNumber?: string;
  receiptCode?: string;
  orderType: OrderType;
  salesRequestNumber: string;
  vendor: string;
  vendorName: string;
  discounts?: Array<Discount>;
  shippingCost?: number;
  insuranceCost: number;
  currency?: string;
  orderEntryList: Array<OrderEntryRequest>;
  status: OrderStatus;
  extraFinancingPercentage?: number;
  condition: string;
  customerCredityType?: string;
  creditProcedure?: string;
  appliedPromotions: Array<string>;
  printingAuthorizationKey?: string;
  originalInvoiceDate?: Date;
  originalExternalInvoices?: string;
  reserved?: Boolean;
  repairOrder?: string;
  repairSerial?: string;
  repairMakeId?: string;
  repairColor?: string;
  repairKms?: string;
  repairModel?: string;
  selfInvoiceSurcharge?: number;
  tax?: number;
  paymentCondition?: string;
  isDummy?: Boolean;
  contractNumber?: string;
  isThirtyDays: boolean;
  paymentResponsible: string;
  paymentResponsibleName: string;
  orderCondition?: number;
  physicalEstablishmentCode: string;
  observations: string;
  procedureId: string;
  origin: string;
  creditReference?: Boolean;
  preApproveCode: string;
  apapplyRateManually: boolean;
  channel: OrderChannel;
  channelType: OrderChannelType;

  constructor() {
    this.insuranceCost = 0;
    this.orderEntryList = [];
    this.orderType = OrderType.REGULAR;
    this.vendor = '';
    this.vendorName = '';
    this.salesOrgCode = '';
    this.branchCode = '';
    this.customerCode = '';
    this.customerName = '';
    this.customerLastName = '';
    this.customerFiscalId = '';
    this.condition = 'contado';
    this.code = '';
    this.appliedPromotions = [];
    this.salesRequestNumber = '';
    this.status = OrderStatus.NONE;
    this.isThirtyDays = false;
    this.paymentResponsible = '';
    this.paymentResponsibleName = '';
    this.physicalEstablishmentCode = '';
    this.observations = '';
    this.procedureId = '';
    this.origin = 'retail';
    this.preApproveCode = '';
    this.apapplyRateManually = false;
    this.channel = OrderChannel.POS
    this.channelType = OrderChannelType.REGULAR;
  }

  buildOrderRequest(
    productCart: Array<ProductCart>,
    taxes: Array<Tax>,
    customer: CustomerTableModel,
    seller: CustomerTableModel,
    branchCode: string,
    salesOrgCode: string,
    exoneration: TaxExemption,
    discount: Discount,
    activePromotions: Array<string>,
    physicalEstablishmentCode: string,
    observations: string,
    orderType?: OrderType,
    orderStatus?: OrderStatus,
    orderTypeId?: number,
    franchise?: Franchise,
    creditProcedureId?: string,
    preApproveCode?: string,
    apapplyRateManually?: boolean,
    creditLineCode?: string,
    cancellationReasonCode?: string,
    isWedding?: boolean
  ) {
    let result = new OrderRequest();

    result.insuranceCost = 0;
    productCart.forEach((f) => {
      let services = productCart.filter((x) => x.referenceCode == f.code);
      let entry = new OrderEntryRequest();
      entry.productCode = f.productCode;
      entry.productName = f.description;
      entry.totalQuantity = f.amount;
      entry.price.amount = f.total;
      entry.price.currency = 'HNL';
      entry.tax = this.getTax(taxes, f, exoneration);
      entry.taxProduct = this.getTax(taxes, f, exoneration);
      entry.code = f.code.toString();
      entry.serialNumber = f.serial;
      entry.services = services.map((m) => m.code.toString());
      f.discount.discountType != DiscountType.NONE &&
        entry.discounts.push(f.discount);
      f.discount2.discountType != DiscountType.NONE &&
        entry.discounts.push(f.discount2);
      entry.isForDelivery = f.isDelivery;
      entry.deliveryBranch = f.deliveryBranchCode;
      entry.associatedOrder = f.associatedOrder != "" ? f.associatedOrder : null;
      entry.associatedOrderEntry = f.associatedOrderEntry != "" && f.associatedOrderEntry != null ? f.associatedOrderEntry.toString() : null;
      entry.extraWarrantyInvoiceCode = f.extraWarrantyInvoiceCode != "" && f.extraWarrantyInvoiceCode != null ? f.extraWarrantyInvoiceCode : null;
      entry.extraWarrantyInvoiceLine = f.extraWarrantyInvoiceLine != "" && f.extraWarrantyInvoiceLine != null ? f.extraWarrantyInvoiceLine : null;
      entry.associatedSerialNumber = f.associatedSerialNumber != "" && f.associatedSerialNumber != null ? f.associatedSerialNumber : null;
      entry.setCode = f.comboProduct;
      entry.wharehouseLocation = f.wharehouseLocation;
      entry.wharehouseNumber = f.wharehouseNumber;
      result.orderEntryList.push(entry);
    });
    result.orderType = orderType || OrderType.REGULAR;
    result.vendor = seller.code;
    result.vendorName = seller.fullName;
    result.customerCode = customer.code;
    result.customerName = customer.fullName;
    result.customerFiscalId = customer.useFiscalId2
      ? customer.fiscalId2
      : customer.fiscalId;
    //result.customerLastName = customer.getMainPersonalInfo().lastName;
    result.branchCode = branchCode;
    result.salesOrgCode = salesOrgCode;
    result.salesRequestNumber = '';
    result.status = orderStatus || OrderStatus.INITIAL;
    result.exonerationDocumentExpirationDate = exoneration.date;
    result.exonerationDocumentId = exoneration.documentId;
    result.exemptionDocumentId = exoneration.documentId2;
    result.exonerationReason = exoneration.reason.reason;
    result.appliedPromotions = activePromotions;
    result.creditProcedure = creditLineCode;
    result.procedureId = creditProcedureId || '';
    result.preApproveCode = preApproveCode || '';
    if (!(discount.discountType == DiscountType.NONE)) {
      result.discounts = [discount];
    }

    result.orderCondition = orderTypeId;
    switch (orderTypeId as OrderConditions) {
      case OrderConditions.UNTIL_30_DAYS_FRANCHISED:
        result.condition = 'credito';
        result.isThirtyDays = true;
        result.paymentResponsible = franchise!.code;
        result.paymentResponsibleName = franchise!.name;
        break;
      case OrderConditions.MORE_THAN_30_DAYS_FRANCHISED:
        result.condition = 'credito';
        result.isThirtyDays = false;
        result.paymentResponsible = franchise!.code;
        result.paymentResponsibleName = franchise!.name;
        break;
      case OrderConditions.FINAL_CUSTOMER_CREDIT:
        result.condition = 'credito';
        result.isThirtyDays = false;
        break;
      case OrderConditions.CREDIT_QUOTATION:
        result.condition = 'credito';
        result.isThirtyDays = false;
        result.creditReference = true;
        break;
      case OrderConditions.CREDIT_QUOTATION_PRE:
        result.condition = 'credito';
        result.isThirtyDays = false;
        result.creditReference = false;
        break;
      case OrderConditions.CASH_BILLLING:
        result.isThirtyDays = false;
        break;
    }
    result.physicalEstablishmentCode = physicalEstablishmentCode;

    result.observations = observations;
    result.apapplyRateManually = apapplyRateManually || false;
    result.cancellationReasonCode = cancellationReasonCode;

    result.channel = OrderChannel.POS;
    result.channelType = isWedding ? OrderChannelType.WEDDING : OrderChannelType.REGULAR;
    return result;
  }

  buildReturnOrderRequest(
    productCart: Array<ProductCart>,
    taxes: Array<Tax>,
    customer: CustomerTableModel,
    seller: CustomerTableModel,
    branchCode: string,
    salesOrgCode: string,
    exoneration: TaxExemption,
    discount: Discount,
    activePromotions: Array<string>,
    physicalEstablishmentCode: string,
    observations: string,
    orderType?: OrderType,
    orderStatus?: OrderStatus,
    orderTypeId?: number,
    franchise?: Franchise,
    creditProcedureId?: string,
    preApproveCode?: string,
    apapplyRateManually?: boolean,
    creditLineCode?: string,
    cancellationReasonCode?: string,
    isWedding?: boolean,
    order?: Order
  ) {
    let result = new OrderRequest();

    result.insuranceCost = 0;
    result.discounts = [];
    let absoluteDiscountValue = 0.0;
    productCart.forEach((f) => {
      let services = productCart.filter((x) => x.referenceCode == f.code);
      let entry = new OrderEntryRequest();
      entry.productCode = f.productCode;
      entry.productName = f.description;
      entry.totalQuantity = f.inputReturnedAmount;
      entry.price.amount = f.price;
      entry.price.currency = 'HNL';
      entry.tax = this.getTax(taxes, f, exoneration);
      entry.taxProduct = this.getTax(taxes, f, exoneration);
      entry.code = f.code.toString();
      entry.serialNumber = f.serial;
      entry.services = services.map((m) => m.code.toString());
      f.discount.discountType != DiscountType.NONE &&
        entry.discounts.push(f.discount);
      f.discount2.discountType != DiscountType.NONE &&
        entry.discounts.push(f.discount2);
      if (order) {
        let totalOfDiscount: number = order.orderEntryList.filter(oe => oe.tax.code != "A6").reduce((a, b) => a + b.totals.netTotal, 0);
        if (totalOfDiscount > 0) {
          absoluteDiscountValue += CalculationUtils.round(((f.total / totalOfDiscount) * (discount.discountValue ?? 0)), 2);
        }
      }
      entry.isForDelivery = f.isDelivery;
      entry.deliveryBranch = f.deliveryBranchCode;
      entry.associatedOrder = f.associatedOrder != "" ? f.associatedOrder : null;
      entry.associatedOrderEntry = f.associatedOrderEntry != "" && f.associatedOrderEntry != null ? f.associatedOrderEntry.toString() : null;
      entry.extraWarrantyInvoiceCode = f.extraWarrantyInvoiceCode != "" && f.extraWarrantyInvoiceCode != null ? f.extraWarrantyInvoiceCode : null;
      entry.extraWarrantyInvoiceLine = f.extraWarrantyInvoiceLine != "" && f.extraWarrantyInvoiceLine != null ? f.extraWarrantyInvoiceLine : null;
      entry.associatedSerialNumber = f.associatedSerialNumber != "" && f.associatedSerialNumber != null ? f.associatedSerialNumber : null;
      entry.setCode = f.comboProduct;
      entry.wharehouseLocation = f.wharehouseLocation;
      entry.wharehouseNumber = f.wharehouseNumber;
      result.orderEntryList.push(entry);
    });
    if (!(discount.discountType == DiscountType.NONE) && order) {
      switch (discount.discountType) {
        case DiscountType.ABSOLUTE:
          if (absoluteDiscountValue > 0) {
            const disc = new Discount();
            disc.discountType = DiscountType.ABSOLUTE;
            disc.discountValue = CalculationUtils.round(absoluteDiscountValue, 2);
            result.discounts?.push(disc);
          }
          break;
        case DiscountType.PERCENTAGE:
          const disc = new Discount();
          disc.discountType = DiscountType.PERCENTAGE;
          disc.discountValue = CalculationUtils.round(discount.discountValue ?? 0, 2);
          result.discounts?.push(disc);
          break;
      }
    }
    result.orderType = orderType || OrderType.REGULAR;
    result.vendor = seller.code;
    result.vendorName = seller.fullName;
    result.customerCode = customer.code;
    result.customerName = customer.fullName;
    result.customerFiscalId = customer.useFiscalId2
      ? customer.fiscalId2
      : customer.fiscalId;
    //result.customerLastName = customer.getMainPersonalInfo().lastName;
    result.branchCode = branchCode;
    result.salesOrgCode = salesOrgCode;
    result.salesRequestNumber = '';
    result.status = orderStatus || OrderStatus.INITIAL;
    result.exonerationDocumentExpirationDate = exoneration.date;
    result.exonerationDocumentId = exoneration.documentId;
    result.exemptionDocumentId = exoneration.documentId2;
    result.exonerationReason = exoneration.reason.reason;
    result.appliedPromotions = activePromotions;
    result.creditProcedure = creditLineCode;
    result.procedureId = creditProcedureId || '';
    result.preApproveCode = preApproveCode || '';

    result.orderCondition = orderTypeId;
    switch (orderTypeId as OrderConditions) {
      case OrderConditions.UNTIL_30_DAYS_FRANCHISED:
        result.condition = 'credito';
        result.isThirtyDays = true;
        result.paymentResponsible = franchise!.code;
        result.paymentResponsibleName = franchise!.name;
        break;
      case OrderConditions.MORE_THAN_30_DAYS_FRANCHISED:
        result.condition = 'credito';
        result.isThirtyDays = false;
        result.paymentResponsible = franchise!.code;
        result.paymentResponsibleName = franchise!.name;
        break;
      case OrderConditions.FINAL_CUSTOMER_CREDIT:
        result.condition = 'credito';
        result.isThirtyDays = false;
        break;
      case OrderConditions.CREDIT_QUOTATION:
        result.condition = 'credito';
        result.isThirtyDays = false;
        result.creditReference = true;
        break;
      case OrderConditions.CREDIT_QUOTATION_PRE:
        result.condition = 'credito';
        result.isThirtyDays = false;
        result.creditReference = false;
        break;
      case OrderConditions.CASH_BILLLING:
        result.isThirtyDays = false;
        break;
    }
    result.physicalEstablishmentCode = physicalEstablishmentCode;

    result.observations = observations;
    result.apapplyRateManually = apapplyRateManually || false;
    result.cancellationReasonCode = cancellationReasonCode;

    result.channel = OrderChannel.POS;
    result.channelType = isWedding ? OrderChannelType.WEDDING : OrderChannelType.REGULAR;
    if (order) {
      result.paymentResponsible = order.paymentResponsible;
      result.paymentResponsibleName = order.paymentResponsibleName;
    }
    return result;
  }

  private getTax(taxes: Tax[], f: ProductCart, exoneration: TaxExemption): Tax {
    let tax = exoneration.reason.taxType
      ? exoneration.reason.notReplace == f.taxCode
        ? f.taxCode
        : exoneration.reason.taxType
      : f.originalTaxCode;

    return taxes.find((t) => t.code == tax) || new Tax();
  }
}
